import React from 'react';
import moment from 'moment';
import { DateTime } from 'luxon';
import titleOnlyConfigure from './titleOnlyConfigure';
import TitleRow from './TitleRow';

import OnTraccrDatePickerRedesign from '../../../common/inputs/OnTraccrDatePickerRedesign';

import ScheduleDateRepeatRedesign from '../../../schedule/ScheduleDateRepeatRedesign';
import FieldTriggerFlag from './FieldTriggerFlag';
import { localToZoneTS, zoneToLocalTS } from '../../../clock/ManualEntry/manualEntryHelpers';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';

const rowStyle = {
  marginTop: 10,
  marginBottom: 10,
  marginRight: 0,
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
};

const configure = ({
  id,
  setConfigProps,
  configProps = {},
  setCanSubmit,
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  contactAddressBooks = {},
  labels = [],
  buckets = [],
  isV3Form = false,
} = {}) => {
  const titleOnlyConfig = titleOnlyConfigure({
    id,
    setConfigProps,
    configProps,
    setCanSubmit,
    sections,
    isExternalForm,
    templateId,
    divisions,
    projectId,
    name,
    isBoardCards,
    setFieldTriggerEditable,
    disableOptional,
    customers,
    projects,
    users,
    costcodes,
    phases,
    projectIdMap,
    vendors,
    equipment,
    formTemplates,
    contactAddressBooks,
    labels,
    buckets,
    isV3Form,
  });

  const updateCheckbox = (key) => (checked) => {
    setConfigProps({
      ...configProps,
      [key]: checked,
    });
  };

  return (
    <>
      {titleOnlyConfig}
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        label="Single Date"
        value={configProps.singleDay}
        onChange={updateCheckbox('singleDay')}
        hoverNode="Check this box to limit selection to time on a single date"
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        label="Hide All Day Button"
        value={configProps.hideAllDay}
        onChange={updateCheckbox('hideAllDay')}
        hoverNode="Check this box to hide the All Day button from the field"
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        label="Hide Time"
        value={configProps.hideTime}
        onChange={updateCheckbox('hideTime')}
        hoverNode="Check this box to hide time selection"
      />
    </>
  );
};

const preview = ({
  id,
  configProps = {},
  responses = {}, // Actively responding
  previewProps = {}, // Completed form
  setResponses,
  responding = false,
  sections,
  projectId,
  templateId,
  divisions,
  isDisplay,
  onFieldTrigger,
  isFormBuilder,
} = {}) => {
  const isFieldDisplay = !isFormBuilder && isDisplay;
  const {
    optional,
    title = 'Title goes here',
    fieldTrigger,
    hideAllDay = false,
    hideTime = false,
    singleDay = false,
    width: fieldWidthConfig = '100',
  } = configProps;

  const isHalfWidth = fieldWidthConfig === '50';

  const {
    [id]: ourResponses = {},
  } = responses;

  const {
    startTime,
    endTime,
    repeat,
    repeatEndDate,
    timezone = DateTime.local().zoneName,
  } = responding ? ourResponses : previewProps;

  const parsedRepeatEndDate = repeatEndDate
    ? moment(zoneToLocalTS(repeatEndDate, timezone))
    : undefined;

  const updateData = (newData) => {
    if (!setResponses) return;
    const { [id]: oldResponses = {} } = responses;
    setResponses({
      ...responses,
      [id]: {
        ...oldResponses,
        ...newData,
        timezone,
      },
    });
  };

  const onRepeatEndDateChanged = (newRepeatEndDate) => {
    let parsedEndDate = null;
    if (moment.isMoment(newRepeatEndDate)) {
      parsedEndDate = localToZoneTS(newRepeatEndDate.valueOf(), timezone);
    }
    updateData({ repeatEndDate: parsedEndDate });
  };

  return (
    <div>
      <TitleRow
        title={title}
        optional={optional}
        isFieldDisplay={isFieldDisplay}
        filter={
          fieldTrigger && !isDisplay
            ? (
              <FieldTriggerFlag
                sections={sections}
                templateId={templateId}
                projectId={projectId}
                divisions={divisions}
                configProps={configProps}
                responding={responding}
                onFieldTrigger={onFieldTrigger}
              />
            ) : null
        }
      />
      <OnTraccrDatePickerRedesign
        onChange={([newStart, newEnd]) => updateData({ startTime: newStart, endTime: newEnd })}
        value={[startTime, endTime]}
        allowClear
        style={rowStyle}
        timezone={timezone}
        hideCheckbox={hideAllDay}
        hideTime={hideTime}
        singleDay={singleDay}
        isFieldDisplay={isFieldDisplay}
        isHalfWidth={isHalfWidth}
      />
      <ScheduleDateRepeatRedesign
        startTime={startTime}
        value={repeat}
        onChange={(newRepeat) => updateData({ repeat: newRepeat })}
        timezone={timezone}
        onRepeatEndDateChanged={onRepeatEndDateChanged}
        parsedRepeatEndDate={parsedRepeatEndDate}
        style={rowStyle}
        isFieldDisplay={isFieldDisplay}
      />
    </div>
  );
};

export default {
  configure,
  preview,
  title: 'Date/Time Range',
  description: 'This field allows users to pick from a date or time range',
};
